import { h, Component, Fragment } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import PaymentAmount from './paymentAmount';
import { isNativeApp, getNativeReturnScheme } from '../../lib/nativeApp';
import { NATIVE_OPEN_URL_EVENT } from '../../lib/nativeDeepLink';

// localStorage key used to resume a pending Vipps payment if the PWA is remounted/reloaded
// (iOS may evict a backgrounded PWA while the user approves in the Vipps app).
const PENDING_KEY = 'vippsPendingPayment';

@observer
class VippsGeneric extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currentPayment: null,
            paymentInProgress: false,
            errorMessage: null,
            waitingForApproval: false,
            redirectUrl: null,
        };
        this.settled = false;
        this.tracking = false;
        this.checking = false;
        this.currentPaymentUuidv4 = null;
        // Inside a Cordova store app (any build): Vipps cannot return the user to this WebView
        // session by https, so we behave like the PWA path (neutral landing page + polling).
        this.isNative = isNativeApp();
        // Custom URL scheme of the store app we run in (null on the web and on old builds).
        // When set, the backend uses <scheme>://vipps-return/<uuid> as the Vipps returnUrl so
        // the Vipps app switches straight back into this app.
        this.nativeScheme = getNativeReturnScheme();
    }

    requestPaymentFn = (params) => {
        const { requestPayment } = this.props;
        const { vippsStore } = this.props.stores;
        return typeof requestPayment === 'function' ? requestPayment(params) : vippsStore.requestPayment(params);
    }

    getPaymentStatusFn = (uuidv4) => {
        const { getPaymentStatus } = this.props;
        const { vippsStore } = this.props.stores;
        return typeof getPaymentStatus === 'function' ? getPaymentStatus(uuidv4) : vippsStore.getPaymentStatus(uuidv4);
    }

    isPwaStandalone = () => {
        return window.navigator.standalone === true
            || window.matchMedia('(display-mode: standalone)').matches;
    }

    // Contexts where Vipps cannot return the user to this very session: an installed PWA (iOS
    // hands control to Safari) or a native app (returns via custom scheme, or via Safari on old
    // builds). Both need the neutral landing page plus in-app polling.
    isSessionlessReturn = () => this.isPwaStandalone() || this.isNative;

    loadAll = async (props = this.props) => {
        const { vippsStore } = this.props.stores;
        vippsStore.updateKeyValue('redirectUrl', null);
    }

    persistPending = (paymentUuidv4) => {
        try {
            window.localStorage.setItem(PENDING_KEY, JSON.stringify({
                paymentUuidv4,
                sale: this.props.sale || null,
                ts: Date.now(),
            }));
        } catch (err) {
            // localStorage may be unavailable (private mode) — in-session polling still works.
        }
    }

    readPending = () => {
        try {
            const raw = window.localStorage.getItem(PENDING_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (err) {
            return null;
        }
    }

    clearPending = () => {
        try {
            window.localStorage.removeItem(PENDING_KEY);
        } catch (err) {
            // ignore
        }
    }

    // Pre-create the Vipps payment so the orange button can be a real <a href> the user taps in a
    // SINGLE gesture. Vipps blocks the universal-link app-switch for programmatic navigation
    // (window.location.href, or a post-await window.open → blank "about:blank" page), so the
    // navigation MUST come from a genuine tap — which means we need the redirectUrl ready first.
    // https://developer.vippsmobilepay.com/docs/knowledge-base/app-flow/
    createPayment = async () => {
        const { amount, title, type, customer, race, raceClassContestant, sale, convention, conventionParticipantId } = this.props;

        if (!amount || amount <= 0) {
            return;
        }

        this.setState({ paymentInProgress: true, errorMessage: null });
        this.settled = false;
        this.tracking = false;

        // On iOS the PWA returns the user to Safari (a session-less sandbox) — not back into the
        // PWA — so send Safari to a neutral public page instead of the authenticated checkout.
        const wantedPage = this.isSessionlessReturn()
            ? `${window.location.href.split('#')[0]}#/window/payment-done`
            : window.location.href;

        try {
            const response = await this.requestPaymentFn({
                amount,
                title,
                type,
                customer,
                race,
                raceClassContestant,
                sale,
                convention,
                conventionParticipantId,
                wantedPage,
                ...(this.nativeScheme ? { returnScheme: this.nativeScheme } : {}),
            });

            if (response?.status !== 200 || !response?.data?.redirectUrl) {
                this.setState({
                    paymentInProgress: false,
                    errorMessage: 'payment.vipps-create-failed',
                });
                return;
            }

            const { paymentUuidv4, redirectUrl } = response.data;
            this.currentPaymentUuidv4 = paymentUuidv4;

            // Background tracking (poll + persist) and the parent "payment started" signal begin
            // only when the user actually taps to open Vipps — see handleOpenVipps — so we don't
            // poll payments the user never starts or hide the payment-method selector prematurely.
            this.setState({
                paymentInProgress: false,
                redirectUrl,
            });
        } catch (err) {
            console.error('[VippsGeneric] Payment error:', err);
            this.setState({
                paymentInProgress: false,
                errorMessage: 'payment.vipps-create-failed',
            });
        }
    }

    // Fired by the user's tap on the Vipps link. The native <a href> navigation proceeds on its
    // own (a genuine gesture → reliable app-switch); here we only signal the parent that payment
    // has started and, on PWA, begin background tracking so we can reconcile when the user returns.
    handleOpenVipps = () => {
        const { callback = () => {} } = this.props;
        if (!this.currentPaymentUuidv4) {
            return;
        }
        callback({ paymentUuidv4: this.currentPaymentUuidv4 });
        if (this.isSessionlessReturn()) {
            this.tracking = true;
            this.persistPending(this.currentPaymentUuidv4);
            this.setState({ waitingForApproval: true });
            this.startPolling(this.currentPaymentUuidv4);
        }
    }

    startPolling = (paymentUuidv4) => {
        const { vippsStore } = this.props.stores;
        this.currentPaymentUuidv4 = paymentUuidv4;

        vippsStore.pollPaymentStatus(paymentUuidv4, {
            onUpdate: (data) => {
                if (data?.paymentStatus >= 1) {
                    this.setState({ currentPayment: data });
                }
            },
            onComplete: data => this.finalizePayment(data),
            onFailed: data => this.failPayment(data),
            maxAttempts: 90,
            interval: 3000,
            getStatus: this.getPaymentStatusFn,
        });
    }

    // Immediately re-check payment status. iOS freezes the setTimeout poll while the PWA is
    // backgrounded during the Vipps hop, so we re-check the moment the PWA becomes visible
    // again rather than waiting for the (possibly frozen) timer to wake up.
    checkPaymentNow = async () => {
        if (this.settled || !this.tracking || !this.currentPaymentUuidv4 || this.checking) {
            return;
        }
        this.checking = true;
        try {
            const response = await this.getPaymentStatusFn(this.currentPaymentUuidv4);
            const status = response?.data?.paymentStatus;
            if (status === 2 || status === 3) {
                await this.finalizePayment(response.data);
            } else if (status >= 90) {
                this.failPayment(response.data);
            } else if (response?.status === 404) {
                // Payment is no longer pending (cancelled/aborted/expired or removed). Don't hang on
                // the "checking" spinner — surface it as failed so the user gets the retry button.
                this.failPayment({ cancelled: true });
            } else if (status >= 1) {
                this.setState({ currentPayment: response.data });
            }
        } finally {
            this.checking = false;
        }
    }

    finalizePayment = async (data) => {
        if (this.settled) {
            return;
        }
        this.settled = true;
        this.clearPending();
        this.setState({
            waitingForApproval: false,
            redirectUrl: null,
            currentPayment: { ...data, paymentStatus: data.paymentStatus },
        });
        const { callbackPaymentDone } = this.props;
        if (callbackPaymentDone) {
            await callbackPaymentDone(data);
        }
    }

    failPayment = (data) => {
        if (this.settled) {
            return;
        }
        this.settled = true;
        this.clearPending();
        this.setState({
            waitingForApproval: false,
            redirectUrl: null,
            errorMessage: data?.timeout
                ? 'payment.vipps-payment-timeout'
                : 'payment.vipps-payment-cancelled',
        });
    }

    handleVisibility = () => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
            return;
        }
        this.checkPaymentNow();
    }

    // cordova-ios calls window.handleOpenURL when the Vipps app returns via our custom scheme;
    // lib/nativeDeepLink.js re-dispatches it as this window event. Only react to our payment.
    handleNativeOpenUrl = (event) => {
        const url = event?.detail?.url;
        if (typeof url !== 'string' || !this.currentPaymentUuidv4) {
            return;
        }
        if (url.indexOf(`vipps-return/${this.currentPaymentUuidv4}`) === -1) {
            return;
        }
        this.checkPaymentNow();
    }

    componentDidMount() {
        this.loadAll();

        document.addEventListener('visibilitychange', this.handleVisibility);
        window.addEventListener('focus', this.handleVisibility);
        window.addEventListener(NATIVE_OPEN_URL_EVENT, this.handleNativeOpenUrl);

        // Resume a pending PWA payment after a remount / cold reload of the same checkout
        // (e.g. iOS evicted the PWA while the user approved in the Vipps app).
        let resumed = false;
        if (this.isSessionlessReturn()) {
            const pending = this.readPending();
            const fresh = pending && (!pending.ts || (Date.now() - pending.ts) < 15 * 60 * 1000);
            const sameSale = pending && (!pending.sale || !this.props.sale || pending.sale === this.props.sale);
            if (pending?.paymentUuidv4 && fresh && sameSale) {
                this.settled = false;
                this.tracking = true;
                this.setState({ waitingForApproval: true });
                this.startPolling(pending.paymentUuidv4);
                this.checkPaymentNow();
                resumed = true;
            } else if (pending && (!fresh || !sameSale)) {
                this.clearPending();
            }
        }

        // One-tap UX: pre-create the payment on mount so the orange button is a real <a href> the
        // user can tap in a single gesture (required for the Vipps app-switch to fire).
        if (!resumed) {
            this.createPayment();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('visibilitychange', this.handleVisibility);
        window.removeEventListener('focus', this.handleVisibility);
        window.removeEventListener(NATIVE_OPEN_URL_EVENT, this.handleNativeOpenUrl);
    }

    render() {
        const { amount, title, currency } = this.props;
        const { currentPayment = {}, paymentInProgress, errorMessage, waitingForApproval, redirectUrl } = this.state;
        const isPwa = this.isPwaStandalone();
        const sessionless = this.isSessionlessReturn();

        const progress = {
            0: 0,
            1: 33,
            2: 66,
            3: 100,
        };
        // Clamp to the real progress range — terminal statuses (90/91/99) must NOT light up the
        // bar (e.g. 91 >= 2/3 would otherwise mark it as "Paid").
        const ps = currentPayment?.paymentStatus;
        const progressStatus = (typeof ps === 'number' && ps >= 0 && ps <= 3) ? ps : 0;
        const conventionId = this.props.convention;

        return (
			<div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 20px;'>

                    <div class='d-flex flex-row justify-content-center mt-0'>
                        <PaymentAmount
                            stores={this.props.stores}
                            title={title}
                            amount={amount}
                            currency={currency}
                        />
                    </div>

                    {(ps === 2 || ps === 3) ? <>
                        {ps === 2 && <>
                            <div class='d-flex flex-column'>
                                <div class='d-flex flex-row justify-content-center display-1'>
                                    <i class='fa-duotone fa-circle-dot fa-bounce' />
                                </div>
                                <div class='d-flex flex-row justify-content-center mt-3'>
                                    <nobr><Text id='payment.vipps-payment-authorized-by-user'>Payment is authorized by Vipps.</Text></nobr>
                                </div>
                            </div>
                        </>}

                        {ps === 3 && <>
                            <div class='d-flex flex-column'>
                                <div class='d-flex flex-row justify-content-center display-1 text-success'>
                                    <i class='fa-duotone fa-check fa-beat' />
                                </div>
                                <div class='d-flex flex-row justify-content-center mt-3'>
                                    <nobr><Text id='payment.vipps-payment-done'>Payment is done.</Text></nobr>
                                </div>
                            </div>
                        </>}
                    </> : <>
                        {errorMessage ? <>
                            <div class='d-flex flex-column align-items-center mt-3'>
                                <div class='display-1 mb-3 text-danger'>
                                    <i class='fa-duotone fa-circle-xmark' />
                                </div>
                                <div class='alert alert-danger text-center w-100'>
                                    <Text id={errorMessage}>Something went wrong. Please try again.</Text>
                                </div>
                                <button
                                    type='button'
                                    class='btn p-0 border-0'
                                    style='background: transparent;'
                                    onClick={this.createPayment}
                                >
                                    <img src={'/assets/external/Pay_pill - NO.png'} style='width: 180px; cursor: pointer;' />
                                </button>
                                {conventionId > 0 && (
                                    <a class='btn btn-outline-secondary rounded-pill mt-3' href={`/convention/${conventionId}`}>
                                        <i class='fa-duotone fa-arrow-left me-2' />
                                        <Text id='checkout.back-to-convention'>Back to convention</Text>
                                    </a>
                                )}
                            </div>
                        </> : redirectUrl ? <>
                            <div class='d-flex flex-column align-items-center mt-3'>
                                <p class='fs-5 text-center'>
                                    <Text id='payment.vipps-open-instruction'>Tap the button to open Vipps and pay.</Text>
                                </p>
                                <a
                                    href={redirectUrl}
                                    target={isPwa ? '_blank' : '_self'}
                                    rel='noopener noreferrer'
                                    class='btn p-0 border-0'
                                    style='background: transparent;'
                                    onClick={this.handleOpenVipps}
                                >
                                    <img src={'/assets/external/Pay_pill - NO.png'} style='width: 180px; cursor: pointer;' />
                                </a>
                                {sessionless && (
                                    <p class='text-body-secondary text-center mt-3'>
                                        <Text id='payment.vipps-return-when-done'>Come back here when you are done.</Text>
                                    </p>
                                )}
                            </div>
                        </> : waitingForApproval ? <>
                            <div class='d-flex flex-column align-items-center mt-3'>
                                <div class='display-1 mb-3'>
                                    <i class='fa-duotone fa-spinner-third fa-spin' />
                                </div>
                                <p class='text-body-secondary text-center'>
                                    <Text id='payment.vipps-checking'>Checking payment status...</Text>
                                </p>
                            </div>
                        </> : paymentInProgress ? <>
                            <div class='d-flex flex-column align-items-center mt-3'>
                                <div class='display-1 mb-3'>
                                    <i class='fa-duotone fa-spinner-third fa-spin' />
                                </div>
                                <p class='text-body-secondary'>
                                    <Text id='payment.vipps-preparing'>Preparing payment...</Text>
                                </p>
                            </div>
                        </> : <>
                            <div class='d-flex flex-column align-items-center mt-0'>
                                <button
                                    type='button'
                                    class='btn p-0 border-0'
                                    style='background: transparent;'
                                    onClick={this.createPayment}
                                >
                                    <img src={'/assets/external/Pay_pill - NO.png'} style='width: 180px; cursor: pointer;' />
                                </button>
                            </div>
                        </>}
                    </>}

                    {!errorMessage && (
                        <div class='d-flex flex-row mt-5'>
                            <div class='progress-bar-container'>
                                <div class='progress-label-container'>
                                    <div class='progress-label'>
                                        <div class='progress-label-text'>Start</div>
                                    </div>
                                    <div class='progress-label'>
                                        <div class='progress-label-text'>Vipps app</div>
                                    </div>
                                    <div class='progress-label'>
                                        <div class='progress-label-text'>Approved</div>
                                    </div>
                                    <div class='progress-label'>
                                        <div class='progress-label-text'>Paid</div>
                                    </div>
                                </div>
                                <div class='progress'>
                                    <div class='progress-bar' style={`width: ${progress[progressStatus]}%`} aria-valuenow='25' aria-valuemin='0' aria-valuemax='100'></div>
                                </div>
                                <div class='progress-dot-container'>
                                    <div class={`progress-dot ${progressStatus >= 0 ? 'active' : ''}`}>&nbsp;</div>
                                    <div class={`progress-dot ${progressStatus >= 1 ? 'active' : ''}`}>&nbsp;</div>
                                    <div class={`progress-dot ${progressStatus >= 2 ? 'active' : ''}`}>&nbsp;</div>
                                    <div class={`progress-dot ${progressStatus >= 3 ? 'active' : ''}`}>&nbsp;</div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        );
    }
}

export default VippsGeneric;
